import {
	Box,
	CircularProgress,
	Divider,
	IconButton,
	TextareaAutosize,
	Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import CustomButton from '../CustomButton/CustomButton';
import CustomDatePicker from '../CustomDatePicker/CustomDatePicker';
import LabeledTextField from '../CustomTextField/LabeledTextField';
import ProductsListing from '../ProductListing/ProductsListing';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import styled from 'styled-components';
import moment from 'moment';
import CustomAlert from '../CustomAlert/CustomAlert';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import { setCart } from '@src/store/reducers/cartReducer';
import {
	ArrowBack,
	CartRemoveIcon,
	ModalCloseIcon,
	SaveForReviewIcon2,
	SaveReviewIcon,
	SlideCancelIcon,
} from '../Icons';
import Warning from '../Warning/Warning';
import Cart from '@src/assets/icons/clear-cart.svg';
import { convertUTCDateIntoIndianTimezone, formatPriceValue } from '@src/helpers/Helpers';
import StoreIcon from '@mui/icons-material/Store';
import usePermission from '@src/hooks/usePermission';
import { ALL_PERMISSION_MAP } from '@src/helpers/Permission';
import CustomTooltip from '../CustomTootip/CustomTooltip';
import CustomDateTimePicker from '../CustomDateTimePicker/CustomDateTimePicker';
import CustomTextButton from '../CustomButton/CustomTextButton';
import { CustomAlertType } from '@src/interfaces/type';
import { IsMobileWidth } from '../MediaQueryUtils';
import ProductCustomCard from '../CustomCard/ProductCustomCard';
import ProductCard from '../CustomCard/ProductCard';
import { RenderCreditWarning, RenderLessAndPayableAmount } from '../CartComponent/CartComponent';

const StyledTextareaAutosize = styled(TextareaAutosize)`
	width: 100%;
	height: 57px !important;
	border: 1.5px solid #e9e9e9;
	margin-top: 8px;
	resize: none;
	border-radius: 4px;
	padding: 2px 8px;
	&:focus-visible {
		outline: #e9e9e9;
	}
	&:hover {
		border-color: #7d7d7d9e;
	}
	&::placeholder {
		color: #acacac;
		font-weight: 600;
		font-family: Nunito Sans;
	}
`;

const IconContainer = styled('div')({
	display: 'flex',
	alignItems: 'center',
});

const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 72px;
	padding: 16px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 10;
	flex-shrink: 0; // Prevents resizing when the content overflows
`;

const ProductContainer = styled.div({
	background: '#F1F1F1',
	paddingTop: '2px',
	// overflowY: 'auto',
	// flexGrow: 1, // Ensure it takes up remaining space and allows scrolling
});

const fontStyles = {
	color: 'var(--Black, #1A1A1A)',
	fontFamily: 'Nunito Sans',
	fontSize: '18px',
	fontWeight: 700,
	lineHeight: '24px',
};

interface CartComponentProps {
	totalItems: number;
	total: number;
	productDetails: any;
	setProductDetails: (details: any) => void;
	setCartData: (data: any) => void;
	checkForCartData: (waId: string, id: string) => void;
	setShowOrderSummary: (show: boolean) => void;
	waId: string;
	restaurantId: string;
	setOpenModal: (open: boolean) => void;
	setResponseMessage: (message: CustomAlertType) => void;
	setWidth: (width: string) => void;
	setApiCall: (call: boolean) => void;
	displayCustomError: (error: string) => void;
	selectedDate: any;
	setSelectedDate: any;
	selectedDateTime: any;
	setSelectedDateTime: any;
	comment: any;
	setComment: any;
	showDetails: any;
	rewardsInfo: any;
	creditInfo: any;
}

function OrderSummary({
	totalItems,
	total,
	productDetails,
	setProductDetails,
	setCartData,
	checkForCartData,
	setShowOrderSummary,
	waId,
	restaurantId,
	setOpenModal,
	setResponseMessage,
	setWidth,
	setApiCall,
	displayCustomError,
	selectedDate,
	setSelectedDate,
	selectedDateTime,
	setSelectedDateTime,
	comment,
	setComment,
	showDetails,
	rewardsInfo,
	creditInfo,
}: CartComponentProps) {
	const cart: any = useAppSelector((state) => state?.cart?.value);
	const selectedBranch: any = useAppSelector((state) => state?.restaurant?.value?.default);
	const dispatch = useAppDispatch();
	const isMobileWidth = IsMobileWidth();

	const [orderBtnLoading, setOrderBtnLoading] = useState(false);
	const [reviewBtnLoading, setReviewBtnLoading] = useState(false);
	const [isDisabled, setIsDisabled] = useState(false);
	const { apiService }: any = React.useContext(ApplicationContext);
	const [error, setError] = useState('');
	const [warning, setWarning] = useState(false);

	//Permissions
	const checkPermission = usePermission();
	const canSaveCart = checkPermission(ALL_PERMISSION_MAP?.RESTAURANT?.CART?.SAVE, waId);
	const canClearCart = checkPermission(ALL_PERMISSION_MAP?.RESTAURANT?.CART?.CLEAR, waId);
	const canViewCart = checkPermission(ALL_PERMISSION_MAP?.RESTAURANT?.CART?.VIEW, waId);
	const canCreateCart = checkPermission(ALL_PERMISSION_MAP?.RESTAURANT?.CART?.UPDATE, waId);

	//Mobile Screen States
	const [windowDimensions, setWindowDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	const updateWindowDimensions = () => {
		setWindowDimensions({
			width: window.innerWidth,
			height: window.innerHeight,
		});
	};

	//For Mobile Screen
	useEffect(() => {
		window.addEventListener('resize', updateWindowDimensions);

		return () => {
			window.removeEventListener('resize', updateWindowDimensions);
		};
	}, []);

	useEffect(() => {
		updateWindowDimensions();
	}, []);

	useEffect(() => {
		const filteredDetails = productDetails.filter((item: any) => item.quantity !== 0);
		setProductDetails(filteredDetails);
	}, []);

	const saveForReview = (data: any, waId: any, id: string) => {
		setReviewBtnLoading(true);
		setIsDisabled(true);

		apiService.saveCartInfo(
			data,
			(response: AxiosResponse) => {
				setResponseMessage({
					message: 'Order saved successfully for review.',
					severity: 'success',
				});
				setOpenModal(false);
				setShowOrderSummary(false);
				checkForCartData(waId, id);
				setReviewBtnLoading(false);
				setIsDisabled(false);
			},
			(error: AxiosError) => {
				console.log(error);
				// setResponseMessage({
				// 	message: 'Something went wrong.',
				// 	severity: 'error',
				// });
				setReviewBtnLoading(false);
				setIsDisabled(false);
				displayCustomError('Something went wrong!');
			},
			selectedBranch?.phone,
			waId,
			id
		);
	};

	const resetCart = () => {
		dispatch(setCart({ ...cart, [waId]: [] }));
	};

	const clearCart = () => {
		const items = productDetails?.map((item: any) => {
			return { id: item?.id || item?.productId, quantity: item?.quantity };
		});
		apiService?.clearCartData(
			items,
			(response: any) => {
				setOpenModal(false);
				setApiCall(true);
				setWidth('1118px');
				setWarning(false);
				setProductDetails([]);
				setShowOrderSummary(false);
			},
			(erro: AxiosError) => {
				// setResponseMessage({
				// 	message: 'Something went wrong.',
				// 	severity: 'error',
				// });
				displayCustomError('Something went wrong!');
			},
			selectedBranch?.phone,
			waId,
			restaurantId
		);
	};

	const submitOrder = async (data: any, waId: any, id: string) => {
		setOrderBtnLoading(true);
		setIsDisabled(true);

		apiService.createOrder(
			data,
			(response: AxiosResponse) => {
				setResponseMessage({
					message: 'Order created successfully.',
					severity: 'success',
				});
				setCartData(null);
				setOpenModal(false);
				setShowOrderSummary(false);
				setOrderBtnLoading(false);
				setIsDisabled(false);
				setWidth('1118px');
				setWarning(false);
				setSelectedDate();

				setProductDetails((prevState: any) => {
					return prevState?.map((item: any) => {
						return { ...item, background: '' };
					});
				});
				clearCart();
			},
			(error: AxiosError) => {
				// setResponseMessage({
				// 	message: 'Something went wrong.',
				// 	severity: 'error',
				// });
				setOrderBtnLoading(false);
				setIsDisabled(false);
				displayCustomError('Something went wrong!');
			},
			selectedBranch?.phone,
			waId
		);
	};
	const maxPriceWarning = () => {
		const warning = productDetails?.some((item: any) =>
			item?.maxPrice && Number(item?.maxPrice) !== 0
				? Number(item?.price) > Number(item?.maxPrice)
				: false
		);
		if (warning) {
			setWarning(true);
			setIsDisabled(true);
			const ids = productDetails?.map((item: any) => {
				const maxPrice = Number(item?.maxPrice);
				const price = Number(item?.price);
				if (maxPrice && maxPrice !== 0 && price > maxPrice) {
					return item?.id;
				}
			});
			setProductDetails((prevState: any) => {
				return prevState?.map((item: any) => {
					if (ids.includes(item?.id)) {
						return { ...item, background: '#FFFDF5' };
					}
					return item;
				});
			});
		} else {
			handleSubmitOrder();
		}
	};

	const isDisableButton = () => {
		return false;
		// return !selectedDate;
	};

	// useEffect(() => {
	// 	if (selectedDate) {
	// 		setIsDisabled(false);
	// 	} else {
	// 		setIsDisabled(true);
	// 	}
	// }, [selectedDate]);

	const handleSaveForReview = () => {
		const data = {
			comments: comment,
			createdAt: selectedDateTime,
		};

		if (selectedDate) {
			//@ts-ignore
			data.requestedDate = moment(new Date(selectedDate as string)).format(
				'YYYY-MM-DDTHH:mm:ss.SSS[Z]'
			);
		}

		saveForReview(data, waId, restaurantId);
	};

	const handleSubmitOrder = () => {
		const data = {
			comments: comment,
			requestedDate: selectedDate
				? moment(new Date(selectedDate as string)).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
				: null,
			createdAt: selectedDateTime,
		};
		submitOrder(data, waId, restaurantId);
	};

	return (
		<Box
			sx={{
				width: '100%',
				height: '572px',
				'@media (max-width: 768px)': {
					width: '100%',
					maxWidth: '100%',
					height: '100%',
					maxHeight: '100%',
				},
			}}
		>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				px={2}
				width="100%"
				height="56px"
			>
				<Box>
					<p style={fontStyles}>Order Summary</p>
				</Box>
				<Box display="flex" justifyContent="flex-end" gap="8px" alignItems="center">
					{windowDimensions?.width > 800 ? (
						<>
							{canCreateCart ? (
								<CustomTextButton
									title="Edit Cart"
									onClick={() => {
										setShowOrderSummary(false);
										setWidth('1118px');
									}}
								/>
							) : null}
							{canClearCart ? (
								<CustomTooltip placement="top" title="Remove All Items from Cart">
									<IconButton
										onClick={() => {
											clearCart();
											setResponseMessage({
												message: 'Cart Clear Successfully',
												severity: 'success',
											});
										}}
									>
										{/* @ts-ignore */}
										<img src={Cart} width="22px" height="20px" alt="shopping-cart" />
									</IconButton>
								</CustomTooltip>
							) : (
								<CustomTooltip
									placement="top"
									title="You do not have permission to clear cart. Contact
				an administrator to get permission."
								>
									<span>
										<IconButton disabled>
											{/* @ts-ignore */}
											<img src={Cart} width="22px" height="20px" alt="shopping-cart" />
										</IconButton>
									</span>
								</CustomTooltip>
							)}
							{canSaveCart ? (
								<CustomTooltip placement="top" title="Save For Review">
									<IconButton onClick={handleSaveForReview}>
										<SaveReviewIcon />
									</IconButton>
								</CustomTooltip>
							) : (
								<CustomTooltip
									placement="top"
									title={`You do not have permission to edit orderbook. Contact
							an administrator to get permission.`}
								>
									<span>
										<IconButton disabled>
											<SaveReviewIcon stroke="#cacaca" />
										</IconButton>
									</span>
								</CustomTooltip>
							)}
							<CustomButton
								// type="submit"
								// title="Submit Order"
								color="primary"
								title={
									orderBtnLoading ? (
										<CircularProgress size={16} style={{ color: 'var(--default-text-color)' }} />
									) : (
										'Submit Order'
									)
								}
								disabled={isDisabled || isDisableButton()}
								handleClick={maxPriceWarning}
								styles={{
									width: '143px',
									height: '36px',
									borderRadius: '4px',
								}}
							/>
						</>
					) : (
						<>
							<CustomButton
								// type="submit"
								// title="Submit Order"
								color="primary"
								title={
									orderBtnLoading ? (
										<CircularProgress size={16} style={{ color: 'var(--default-text-color)' }} />
									) : (
										'Submit Order'
									)
								}
								disabled={isDisabled || isDisableButton()}
								handleClick={maxPriceWarning}
								styles={{
									width: '143px',
									height: '36px',
									borderRadius: '4px',
								}}
							/>
						</>
					)}
					<Box
						sx={{ cursor: 'pointer', marginLeft: '10px' }}
						onClick={() => {
							setOpenModal(false);
							setShowOrderSummary(false);
							setProductDetails([]);
						}}
					>
						<ModalCloseIcon />
					</Box>
				</Box>
			</Box>
			<Divider style={{ width: '100%' }} />
			<Box
				display="flex"
				justifyContent={windowDimensions?.width <= 800 ? 'space-between' : 'flex-end'}
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					p: '20px',
					textAlign: 'end',
				}}
			>
				{windowDimensions?.width <= 800 ? (
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						{canCreateCart ? (
							<CustomTextButton
								title="Edit Cart"
								onClick={() => {
									setShowOrderSummary(false);
									setWidth('1118px');
								}}
							/>
						) : null}
						{canClearCart ? (
							<CustomTooltip placement="top" title="Remove All Items from Cart">
								<IconButton
									onClick={() => {
										clearCart();
										setResponseMessage({ message: 'Cart Clear Successfully', severity: 'success' });
									}}
								>
									{/* @ts-ignore */}
									<img src={Cart} width="22px" height="20px" alt="shopping-cart" />
								</IconButton>
							</CustomTooltip>
						) : (
							<CustomTooltip
								placement="top"
								title="You do not have permission to clear cart. Contact
				an administrator to get permission."
							>
								<span>
									<IconButton disabled>
										{/* @ts-ignore */}
										<img src={Cart} width="22px" height="20px" alt="shopping-cart" />
									</IconButton>
								</span>
							</CustomTooltip>
						)}
						{canSaveCart ? (
							<CustomTooltip placement="top" title="Save For Review">
								<IconButton onClick={handleSaveForReview}>
									<SaveReviewIcon />
								</IconButton>
							</CustomTooltip>
						) : (
							<CustomTooltip
								placement="top"
								title={`You do not have permission to edit orderbook. Contact
							an administrator to get permission.`}
							>
								<span>
									<IconButton disabled>
										<SaveReviewIcon stroke="#cacaca" />
									</IconButton>
								</span>
							</CustomTooltip>
						)}
					</Box>
				) : null}

				{windowDimensions?.width > 800 ? (
					<CustomDateTimePicker
						label="Select Date and Time"
						styles={{ width: '297px', height: '36px' }}
						selectedDateTime={selectedDateTime}
						onDateTimeChange={(dateTime) => {
							setSelectedDateTime(dateTime);
						}}
						disabled={true}
						windowDimensions={windowDimensions}
						maxDateTime={selectedDate}
					/>
				) : null}

				<Box>
					<Typography variant="body2" sx={{ mb: 1 }}>
						Total Qty: {totalItems || 0} items
					</Typography>
					<Typography variant="body2" sx={{ fontWeight: '700' }}>
						Total Amount: ₹ {formatPriceValue(total) || 0}
					</Typography>
					<RenderLessAndPayableAmount rewardsInfo={rewardsInfo} total={total} />
				</Box>
			</Box>
			<Box sx={{ p: '16px' }}>
				{warning ? (
					<Warning
						onClick={handleSubmitOrder}
						message="The prices of some items have been updated by the supplier. Please review the new prices before confirming your order."
						btnStyles={{ width: '215px' }}
						tootip={!selectedDate}
						tooltipTitle="Select date first"
					/>
				) : null}
			</Box>
			<RenderCreditWarning creditInfo={creditInfo} rewardsInfo={rewardsInfo} total={total} />
			<form style={{ width: '100%' }}>
				<Box sx={{ px: '16px' }}>
					{windowDimensions?.width <= 800 ? (
						<Box sx={{ marginBottom: '16px' }}>
							<CustomDateTimePicker
								label="Select Date and Time"
								styles={{ width: '100%', height: '36px' }}
								selectedDateTime={selectedDateTime}
								onDateTimeChange={(dateTime) => {
									setSelectedDateTime(dateTime);
								}}
								disabled={true}
								windowDimensions={windowDimensions}
								maxDateTime={selectedDate}
							/>
						</Box>
					) : null}
					<Typography variant="body2" sx={{ mt: '-6px', color: 'var(--color-font-grey)' }}>
						Request Delivery Date
					</Typography>
					<CustomDatePicker
						styles={{ width: '100%' }}
						selectedDate={selectedDate}
						onDateChange={(newDate) => {
							//Getting date in UTC, adding minutes according to indian timezone diff
							setSelectedDate(convertUTCDateIntoIndianTimezone(newDate as Date));
						}}
						minDate={selectedDateTime}
					/>
					{/* <Typography variant="body2" sx={{ mt: '16px', color: 'var(--color-font-grey)' }}>
						Leave a comment
					</Typography>
					<StyledTextareaAutosize
						value={comment}
						onChange={(e) => setComment(e.target.value)}
						placeholder="leave a comment"
						maxLength={200}
					/> */}
					<Box sx={{ marginTop: '8px' }}>
						<LabeledTextField
							label="Leave a comment"
							value={comment}
							onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
								setComment(e.target.value as string)
							}
							multiline
							rows={4}
							placeholder="leave a comment"
							styles={{ width: '100%' }}
						/>
					</Box>
				</Box>
				<Box sx={{ p: '16px', pb: '20px' }}>
					<Box display="flex" width="100%" justifyContent="space-between" sx={{ mb: '16px' }}>
						<Typography variant="body1">Your Products</Typography>
						<Typography variant="body2" style={{ color: 'var(--lighter-text-color)' }}>
							{productDetails?.length} Products
						</Typography>
					</Box>
					{/* <ProductsListing products={productDetails} /> */}
					<div
						style={{ height: isMobileWidth ? 'unset' : 'calc(100vh - 500px)', overflow: 'auto' }}
					>
						{productDetails?.map((product: any) => {
							return (
								<ProductCard
									//@ts-ignore
									data={product}
									noCheckbox
									view="order"
									showDetails={showDetails}
									readOnly
								/>
							);
						})}
					</div>
				</Box>
				<Divider style={{ width: '100%' }} />
			</form>
		</Box>
	);
}

export default OrderSummary;
